import _ from 'lodash';
import request from 'superagent';
import { receiveError as receivePacerError } from './pacerSearch';
import * as types from '../actionTypes/irSearchActionTypes';
import * as errorActionTypes from '../actionTypes/errorActionTypes';
import * as documentsActionTypes from '../actionTypes/claimDocumentsActionTypes';
import { notify } from '../../components/common/Notification';

function mapResponseErrorsToMessage(err) {
  const request_id = _.get(err, ['response', 'body', 'error', 'request_id']);
  const errorMessageExtra = request_id ? `When contacting support, please quote Request ID '${request_id}'` : '';

  return {
    // We want to hide real messages from user since traces are confusing to them
    // TODO: coordinate with API to surface proper errors such as field validation?
    messages: ['PACER is experiencing some issues. Please try again in a few minutes.', errorMessageExtra],
    // but we include the real message in the details so we receive it in the sentry report
    details: _.get(err, ['response', 'body', 'error']) || [],
  };
}

export function receiveError(err, msg) {
  return {
    type: types.SERVICE_ERROR,
    error: msg,
    errors: msg,
  };
}

export function onFailure(dispatch) {
  return function(err) {
    console.error(err);
    return dispatch(receiveError(types.SERVICE_ERROR, mapResponseErrorsToMessage(err)));
  };
}

// TODO: move to clientCode actions file
export const checkClientCodeValid = () =>
  function(dispatch) {
    if (!window.client_code_is_valid()) {
      dispatch(
        receivePacerError(errorActionTypes.VALIDATION_ERROR, { messages: ['Please set a valid client code first.'] })
      );
      return;
    }
    dispatch({
      type: errorActionTypes.RESET_VALIDATION_ERROR,
    });
    return true;
  };

export const getPartsPurchaseInfo = params => (dispatch, getState) => {
  function onSuccess(response) {
    if (!_.isEmpty(response.body.error)) {
      return onFailure(dispatch)({ response });
    }

    const { entries } = getState()
      .get('dockets')
      .toJS();

    const updatedEntriesData = entries.data.map(docket => {
      // default to in-pacer
      docket.colorCode = 'in-pacer';

      const { seqNum } = docket;
      const responseEntry = _.find(response.body, { sequence_number: seqNum });

      if (responseEntry) {
        if (responseEntry.has_user_purchased_any_document) {
          docket.colorCode = 'purchased';
        } else {
          docket.colorCode = 'in-inforuptcy';
        }
      }
      return docket;
    });

    dispatch({
      type: documentsActionTypes.UPDATE_CLAIMS,
      entries: { ...entries, status: 'done', data: updatedEntriesData },
    });

    return Promise.resolve(response.body);
  }

  return request
    .post('/rpc/dockets/purchase_info')
    .set('Accept', 'application/json')
    .send(params)
    .then(onSuccess, onFailure(dispatch));
};

const getDocumentsFromPacer = ({ court_code, case_number, claimNum, seqNum }) => dispatch => {
  dispatch({
    type: documentsActionTypes.FETCH_CLAIM_DOCUMENTS.REQUEST,
    selectedClaim: {
      status: 'loading',
      claimNum,
      seqNum,
      court_code,
      case_number,
    },
  });

  const onError = error => {
    dispatch({
      type: documentsActionTypes.FETCH_CLAIM_DOCUMENTS.ERROR,
      error,
    });

    notify.error(error || 'Failed to load data');
  };

  function onSuccess(response) {
    if (response.body && !_.isEmpty(response.body.error)) {
      onError(response.body.error);
      return onFailure(dispatch)({ response });
    }

    dispatch({
      type: documentsActionTypes.FETCH_CLAIM_DOCUMENTS.SUCCESS,
      selectedClaim: {
        status: 'done',
        claimNum,
        seqNum,
        court_code,
        case_number,
        binder: response.body,
      },
    });
  }

  return request
    .post(`/rpc/capi/cases/pacer/${court_code}/${case_number}/claims/${claimNum}/documents/${seqNum}`)
    .then(onSuccess, onFailure(dispatch))
    .catch(error => onError(error));
};

export const getDocuments = ({ court_code, case_number, claimNum, seqNum }) => (dispatch, getState) => {
  // check for documents in existing entries
  /*
  const cachedDocket = getState()
    .getIn(['dockets', 'entries', 'data'])
    .find(docket => docket.get('claimNum') === claimNum);

  if (cachedDocket && cachedDocket.getIn(['binder', 'documents', 0])) {
    return dispatch({
      type: documentsActionTypes.UPDATE_SELECTED_DOCKET,
      selectedClaim: {
        status: 'done',
        claimNum,
        ...cachedDocket.toJS(),
      },
    });
  }
*/

  // retrieve from pacer
  return dispatch(getDocumentsFromPacer({ court_code, case_number, claimNum, seqNum }));
};

// this is basically just a passthrough from the document response, located in links.order_pdf
// adding it here so we can update the state of the documents to inlcude the download links
export const orderPdf = document => dispatch => {
  if (!(document.links && document.links.order_pdf)) {
    return Promise.reject();
  }

  const url = document.links.order_pdf.href;
  const rpcLinkSegment = `/rpc/${url.split('/rpc/')[1]}`;

  dispatch({
    type: documentsActionTypes.ORDER_PDF_DOCUMENT.REQUEST,
    number: document.number,
  });

  const onError = error => {
    dispatch({
      type: documentsActionTypes.ORDER_PDF_DOCUMENT.ERROR,
      error,
    });

    notify.error(error || 'Failed to load data');
  };

  function onSuccess(response) {
    if (response.body && !_.isEmpty(response.body.error)) {
      onError(response.body.error);
      return onFailure(dispatch)({ response });
    }

    const { document: updatedDocument } = response.body;

    dispatch({
      type: documentsActionTypes.ORDER_PDF_DOCUMENT.SUCCESS,
      document: { ...updatedDocument, status: 'done' },
    });

    return Promise.resolve(updatedDocument);
  }

  return request(rpcLinkSegment)
    .then(onSuccess, onFailure(dispatch))
    .catch(error => onError(error));
};

export const purchaseDockets = params => (dispatch, getState) => {
  const { court_code, case_number } = params;

  dispatch({
    type: documentsActionTypes.UPDATE_CLAIMS,
    header: { status: 'loading' },
    entries: { status: 'loading' },
  });

  const currentState = getState().toJS();
  const clientCode = currentState.clientCodeReducer.sessionClientCode;
  const pacer_client = _.isEmpty(clientCode) ? window.Drupal.settings.clientCode : clientCode;

  function onSuccess(response) {
    if (!_.isEmpty(response.body.error)) {
      return onFailure(dispatch)({ response });
    }

    const receipt = _.get(response.body, 'receipts', {});

    dispatch({
      type: documentsActionTypes.UPDATE_CLAIMS,
      receipt,
    });

    return Promise.resolve(response.body);
  }

  const paramsStringNullRemoved = _.omitBy({ ...params, pacer_client }, _.isNil);

  return (
    request
      .post(`/rpc/capi/cases/pacer/${court_code}/${case_number}/dockets/update`)
      .type('form')
      .send(paramsStringNullRemoved)
      // .set('Accept', 'application/json')
      .then(onSuccess, onFailure(dispatch))
  );
};

// Taken from https://gist.github.com/joepie91/2664c85a744e6bd0629c#gistcomment-1787629
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

function rpcProgress(url, dispatch, getState) {
  return request
    .get(url)
    .set('Accept', 'application/json')
    .then(response => {
      if (response.accepted) {
        // still processing, update percentage so progress bar updates.
        const percentage = Math.floor(response.body.task_progress);

        const state = getState().toJS();

        const docketParts = {
          ...state.dockets.docketParts,
          percentage,
        };

        dispatch({
          type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
          docketParts,
        });

        // wait 0.5s and then update progress again
        return wait(500).then(() => rpcProgress(url, dispatch, getState));
      }

      // when progress finishes, it returns a 302 response to the actual results, but it appears that  superagent
      // follows that redirect for us, so we should have a 200 response here
      if (response.status !== 200) {
        return onFailure(dispatch)({ response });
      }

      return Promise.resolve(response.body);
    }, onFailure(dispatch));
}

export const buyAllParts = params => (dispatch, getState) => {
  const currentState = getState().toJS();
  const prevDocketParts = currentState.dockets.docketParts;
  const clientCode = currentState.clientCodeReducer.sessionClientCode;
  const pacer_client = _.isEmpty(clientCode) ? window.Drupal.settings.clientCode : clientCode;

  params = { ...params, pacer_client };

  dispatch({
    type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      ...prevDocketParts,
      status: 'loading',
      step: 2,
      percentage: 0,
    },
  });

  return request
    .post('/rpc/dockets/documents/purchase')
    .type('json')
    .send(params)
    .then(response => {
      if (!response.accepted || _.isEmpty(response.header.location)) {
        return onFailure(dispatch)({ response });
      }

      return rpcProgress(response.header.location, dispatch, getState);
    }, onFailure(dispatch))
    .then(purchaseResponse => {
      if (purchaseResponse.errors.length > 0) {
        const errors = _.uniq(purchaseResponse.errors.map(({ error }) => error));
        dispatch({
          type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
          docketParts: {
            purchased: purchaseResponse.purchased,
            step: 2,
            percentage: 0,
            status: 'done',
            errors,
          },
        });

        return Promise.reject(purchaseResponse);
      }

      dispatch({
        type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
        docketParts: {
          purchased: purchaseResponse.purchased,
          step: 3,
          percentage: 0,
          errors: null,
          status: 'done',
        },
      });

      return Promise.resolve(purchaseResponse);
    }, onFailure(dispatch));
};

export const updatePopup = popup => dispatch =>
  dispatch({
    type: documentsActionTypes.DOCKETS_POPUP,
    popup,
  });

function downloadDocuments(params, dispatch, getState, type, combine_binders, printPreview = false) {
  const prevDocketParts = getState()
    .getIn(['claimDocuments', 'docketParts'])
    .toJS();
  const prevPopup = getState()
    .getIn(['claimDocuments', 'popup'])
    .toJS();

  dispatch({
    type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      ...prevDocketParts,
      status: 'loading',
      step: 3,
    },
  });

  const deliverParams = {
    court_code: params.court_code,
    case_number: params.case_number,
    docket_numbers: params.docket_numbers,
    delivery_method: 'download',
    type,
    combine_binders,
  };

  return request
    .post('/rpc/dockets/documents/deliver')
    .type('json')
    .send(deliverParams)
    .then(response => {
      if (!response.accepted || _.isEmpty(response.header.location)) {
        return onFailure(dispatch)({ response });
      }
      return rpcProgress(response.header.location, dispatch, getState);
    }, onFailure(dispatch))
    .then(deliverResponse => {
      // what we get here is
      // delivery_method: "download"
      // download_url: "http://..."
      // print_url: "http://..."
      // filename: "..."
      const link = printPreview ? deliverResponse.print_url : deliverResponse.download_url;
      const pretty_name = deliverResponse.filename;

      dispatch({
        type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
        docketParts: {
          ...prevDocketParts,
          email_address: params.email_address,
          step: 3,
          link,
          pretty_name,
        },
      });

      dispatch(updatePopup({ ...prevPopup, data: {} }));

      window.open(link);

      if (params.docket_numbers && params.docket_numbers.length === 1) {
        return dispatch(
          getDocuments({
            court_code: params.court_code,
            case_number: params.case_number,
            seqNum: params.docket_numbers[0],
            claimNum: parseInt(params.docket_numbers[0], 10),
          })
        );
      }

      return Promise.resolve(deliverResponse);
    }, onFailure(dispatch));
}

function emailParts(params, dispatch, getState, type, combine_binders) {
  const { dockets } = getState().toJS();

  dispatch({
    type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      ...dockets.docketParts,
      status: 'loading',
      step: 3,
    },
  });

  const deliverParams = {
    ...params,
    delivery_method: 'email',
    type,
    combine_binders,
  };

  return request
    .post('/rpc/dockets/documents/deliver')
    .type('json')
    .send(deliverParams)
    .then(response => {
      if (!response.accepted || _.isEmpty(response.header.location)) {
        return onFailure(dispatch)({ response });
      }
      return rpcProgress(response.header.location, dispatch, getState);
    }, onFailure(dispatch))
    .then(deliverResponse => {
      const { email_addresses, success } = deliverResponse;

      dispatch(updatePopup({ ...dockets.popup, show: false, data: {} }));

      dispatch({
        type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
        docketParts: {
          ...dockets.docketParts,
          email_address: email_addresses.join(', '),
          step: 3,
          success,
          showFlash: true,
        },
      });

      dispatch({
        type: documentsActionTypes.DOCKETS_FAVORITE_EMAIL,
        favoriteEmail: {
          ...dockets.favoriteEmail,
          status: 'done',
        },
      });

      if (params.docket_numbers && params.docket_numbers.length === 1) {
        return dispatch(
          getDocuments({
            court_code: params.court_code,
            case_number: params.case_number,
            seqNum: params.docket_numbers[0],
            claimNum: parseInt(params.docket_numbers[0], 10),
          })
        );
      }

      return Promise.resolve('success');
    }, onFailure(dispatch));
}

export const downloadPdfZip = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'zip', false);

export const downloadPdfCombinedZipped = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'zip', true);

export const downloadPdfCombined = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'pdf', false);

export const previewPdf = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'pdf', false, true);

export const emailPartsZip = params => (dispatch, getState) => emailParts(params, dispatch, getState, 'zip', false);

export const emailPartsPdfCombined = params => (dispatch, getState) =>
  emailParts(params, dispatch, getState, 'pdf', true);

export const getDocketParts = params => (dispatch, getState) => {
  dispatch({
    type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      step: 1,
      status: 'loading',
      totalParts: 0,
    },
  });

  return request
    .post('/rpc/dockets/documents/cost')
    .type('json')
    .send(params)
    .then(response => {
      if (!response.accepted || _.isEmpty(response.header.location)) {
        return onFailure(dispatch)({ response });
      }

      return rpcProgress(response.header.location, dispatch, getState);
    }, onFailure(dispatch))
    .then(costResponse => {
      const totalCost = parseFloat(costResponse.total.cost);

      const docketParts = {
        partsToPurchase: costResponse.pacer.documents,
        totalParts: costResponse.total.documents,
        totalCost,
        step: 1,
        status: 'done',
      };

      // if no docs need to be purchased from pacer, move to step 2
      if (costResponse.pacer.documents === 0) {
        docketParts.step = 2;
        docketParts.alreadyPurchased = true;
      }

      dispatch({
        type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
        docketParts,
      });
    }, onFailure(dispatch));
};

export const buyPdf = params => dispatch => {
  const { case_no, claimNum, seqNum, partNumber, court_code } = params;

  dispatch({
    type: documentsActionTypes.BUY_PDF_DOCUMENT.REQUEST,
    number: partNumber,
  });

  function onSuccess({ body }) {
    if (!_.isEmpty(body.error)) {
      return dispatch(receiveError(null, { messages: [body.error.errorMessage] }));
    }

    const { document /* , receipt */ } = body;

    dispatch({
      type: documentsActionTypes.BUY_PDF_DOCUMENT.SUCCESS, // UPDATE_CLAIM_DOCUMENT,
      document,
      // TODO: add receipt in correct location (nested in the entry)
      // receipt,
    });

    return Promise.resolve(body);
  }

  return request
    .post(`/rpc/capi/cases/pacer/${court_code}/${case_no}/claims/${claimNum}/documents/${seqNum}/${partNumber}`)
    .set('Accept', 'application/json')
    .then(onSuccess, onFailure(dispatch));
};

export const updateRedact = updatedRedact => (dispatch, getState) => {
  const { redact } = getState()
    .get('dockets')
    .toJS();

  return dispatch({
    type: documentsActionTypes.UPDATE_REDACT,
    redact: { ...redact, ...updatedRedact },
  });
};

export const resetDocketParts = () => dispatch => dispatch({ type: documentsActionTypes.RESET_DOCKETS_PARTS });

export const togglePopup = (params = {}) => dispatch => {
  dispatch(resetDocketParts());

  const popup = params.show ? { ...params } : { show: false };

  dispatch(updatePopup(popup));
};

export const updateDocketParts = docketParts => dispatch =>
  dispatch({
    type: documentsActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts,
  });



// WEBPACK FOOTER //
// ./src/redux/actions/claimDocumentsActions.js